import { StoreApi, UseBoundStore } from 'zustand';

import MuiFlagIcon from '@mui/icons-material/Flag';
import MuiPrintIcon from '@mui/icons-material/Print';
import MuiGroupsIcon from '@mui/icons-material/Groups';
import MuiPersonIcon from '@mui/icons-material/Person';
import MuiRefreshIcon from '@mui/icons-material/Refresh';
import MuiSettingsIcon from '@mui/icons-material/Settings';
import MuiCreditCardIcon from '@mui/icons-material/CreditCard';
import MuiDesktopMacIcon from '@mui/icons-material/DesktopMac';
import MuiReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import MuiPhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';

import { redactSensitiveData } from 'src/utils/security/redact-sensitive-data';

import type { MuiIconComponent } from 'src/components/mui-icon';

import { useSaleStore } from './SalesStore';
import { useSystemStore } from './SystemStore';
import { useApplicationFlags } from './AppFlags';
import { usePrinterStore } from './PrinterStore';
import { useLoadingStore } from './LoadingStore';
// Import all stores - handle mixed export patterns
import { useDeviceSyncStore } from './DeviceSync';
import { useCustomerStore } from './CustomerData';
import { useInvoiceListStore } from './InvoiceData';
import { useCompanionStore } from './CompanionStore';
import { usePreferenceStore } from './PreferenceStore';
import { useDeviceContextStore } from './DeviceContext';

/**
 * Store category for grouping in the viewer
 */
export type StoreCategory = 'core' | 'feature' | 'ui' | 'preference';

/**
 * Metadata for a Zustand store
 */
export interface StoreMetadata {
  name: string;
  description: string;
  category: StoreCategory;
  icon: MuiIconComponent; // MUI icon component
  hook: UseBoundStore<StoreApi<any>>; // The Zustand hook
  getStateOnly: () => Record<string, any>; // Extract only state (no setters)
}

export { redactSensitiveData } from 'src/utils/security/redact-sensitive-data';

/**
 * Filter out setter functions from store state
 * Returns only data properties, not function setters
 */
const filterSetters = (state: any): Record<string, any> => {
  if (!state || typeof state !== 'object') {
    return {};
  }

  const filtered: Record<string, any> = {};

  Object.keys(state).forEach((key) => {
    const value = state[key];

    // Skip functions (setters, actions, etc.)
    if (typeof value === 'function') {
      return;
    }

    // Include everything else (data, primitives, objects, arrays)
    filtered[key] = value;
  });

  return filtered;
};

/**
 * Calculate size of data object
 */
export const calculateDataSize = (data: any): { propertyCount: number; byteSize: number } => {
  if (!data || typeof data !== 'object') {
    return { propertyCount: 0, byteSize: 0 };
  }

  const jsonString = JSON.stringify(data, null, 0);
  const byteSize = new Blob([jsonString]).size;
  const propertyCount = Object.keys(data).length;

  return { propertyCount, byteSize };
};

/**
 * Format byte size to human-readable format
 */
export const formatByteSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

/**
 * Central registry of all Zustand stores
 */
export const STORE_REGISTRY: Record<string, StoreMetadata> = {
  // ========================================
  // CORE STORES (6)
  // ========================================
  system: {
    name: 'System Store',
    description: 'Platform, BLE reader, update management, and feature-flag state',
    category: 'core',
    icon: MuiSettingsIcon,
    hook: useSystemStore,
    getStateOnly: () => {
      const state = useSystemStore.getState();
      const filtered = filterSetters(state);
      return redactSensitiveData(filtered);
    },
  },

  deviceSync: {
    name: 'Device Sync Store',
    description: 'Tenant business, users, devices, terminals, providers, and plans',
    category: 'core',
    icon: MuiPersonIcon,
    hook: useDeviceSyncStore,
    getStateOnly: () => {
      const state = useDeviceSyncStore.getState();
      const filtered = filterSetters(state);
      return redactSensitiveData(filtered);
    },
  },

  deviceContext: {
    name: 'Device Context Store',
    description: 'Resolved business device, location, terminal, and device identity context',
    category: 'core',
    icon: MuiPhoneAndroidIcon,
    hook: useDeviceContextStore,
    getStateOnly: () => {
      const state = useDeviceContextStore.getState();
      const filtered = filterSetters(state);
      return redactSensitiveData(filtered);
    },
  },

  sales: {
    name: 'Sales Store',
    description: 'Active sales management, payment flow, sale items and totals',
    category: 'core',
    icon: MuiCreditCardIcon,
    hook: useSaleStore,
    getStateOnly: () => {
      const state = useSaleStore.getState();
      const filtered = filterSetters(state);
      return redactSensitiveData(filtered);
    },
  },

  preferences: {
    name: 'Preference Store',
    description: 'User preferences: UI, checkout, inventory, receipts',
    category: 'preference',
    icon: MuiSettingsIcon,
    hook: usePreferenceStore,
    getStateOnly: () => {
      const state = usePreferenceStore.getState();
      const filtered = filterSetters(state);
      return redactSensitiveData(filtered);
    },
  },

  // ========================================
  // FEATURE STORES (5)
  // ========================================

  customers: {
    name: 'Customer Data Store',
    description: 'Customer information, contact details, purchase history',
    category: 'feature',
    icon: MuiGroupsIcon,
    hook: useCustomerStore,
    getStateOnly: () => {
      const state = useCustomerStore.getState();
      const filtered = filterSetters(state);
      return redactSensitiveData(filtered);
    },
  },

  invoices: {
    name: 'Invoice Data Store',
    description: 'Invoice management, outstanding/overdue/paid invoices, overviews',
    category: 'feature',
    icon: MuiReceiptLongIcon,
    hook: useInvoiceListStore,
    getStateOnly: () => {
      const state = useInvoiceListStore.getState();
      const filtered = filterSetters(state);
      return redactSensitiveData(filtered);
    },
  },

  printer: {
    name: 'Printer Store',
    description: 'Receipt printing, cash drawer control, printer status',
    category: 'feature',
    icon: MuiPrintIcon,
    hook: usePrinterStore,
    getStateOnly: () => {
      const state = usePrinterStore.getState();
      const filtered = filterSetters(state);
      return redactSensitiveData(filtered);
    },
  },

  companion: {
    name: 'Companion Store',
    description: 'Companion device management (TV, self-checkout, customer-facing, signage)',
    category: 'feature',
    icon: MuiDesktopMacIcon,
    hook: useCompanionStore,
    getStateOnly: () => {
      const state = useCompanionStore.getState();
      const filtered = filterSetters(state);
      return redactSensitiveData(filtered);
    },
  },

  // ========================================
  // UI STORES (3)
  // ========================================
  loading: {
    name: 'Loading Store',
    description: 'Global loading states, modal control, async operation tracking',
    category: 'ui',
    icon: MuiRefreshIcon,
    hook: useLoadingStore,
    getStateOnly: () => {
      const state = useLoadingStore.getState();
      const filtered = filterSetters(state);
      return redactSensitiveData(filtered);
    },
  },

  // ========================================
  // APP STORES (1)
  // ========================================
  appFlags: {
    name: 'App Flags Store',
    description: 'Application feature flags, minimum version, coming soon apps, AI features',
    category: 'core',
    icon: MuiFlagIcon,
    hook: useApplicationFlags,
    getStateOnly: () => {
      const state = useApplicationFlags.getState();
      const filtered = filterSetters(state);
      return redactSensitiveData(filtered);
    },
  },
};

/**
 * Get all store keys
 */
export const getStoreKeys = (): string[] => Object.keys(STORE_REGISTRY);

/**
 * Get store metadata by key
 */
export const getStoreMetadata = (storeKey: string): StoreMetadata | undefined =>
  STORE_REGISTRY[storeKey];

/**
 * Get stores by category
 */
export const getStoresByCategory = (category: StoreCategory): Array<[string, StoreMetadata]> =>
  Object.entries(STORE_REGISTRY).filter(([_, metadata]) => metadata.category === category);
